<script setup>
import {
  ref,
  computed,
  watch,
  nextTick,
  onMounted,
  onUnmounted,
  onDeactivated,
} from 'vue';
import { useI18n } from 'vue-i18n';
import logger from 'shared/helpers/logger';
import PipelinesAPI from 'dashboard/api/pipelines';
import PipelineColumn from './PipelineColumn.vue';
import PipelineHeader from './PipelineHeader.vue';
import { useStore } from 'vuex';
import { usePipelinePermissions } from '../composables/usePipelinePermissions';
import PipelineCardForm from './PipelineCardForm.vue';
import Modal from '../../../../components/Modal.vue';
import PipelineCardDetails from './PipelineCardDetails.vue';
import PipelineFilter from './PipelineFilter.vue';
import { emitter } from 'shared/helpers/mitt';
import PipelineForm from './PipelineForm.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import { PipelineBoardSkeleton } from './skeletons';
import PipelineTour from './PipelineTour.vue';
import { usePipelineTour } from 'dashboard/composables/usePipelineTour';
import LostReasonModal from './LostReasonModal.vue';
import MarkAsWonModal from './MarkAsWonModal.vue';
import { movePipelineColumn } from '../helpers/reorderPipelineColumns';
import { getStageColorClasses, stageColorStyle } from './pipelineTemplates';

const props = defineProps({
  currentView: {
    type: String,
    default: 'pipeline',
  },
  pipelineCards: {
    type: Array,
    required: true,
  },
  isLoading: {
    type: Boolean,
    required: true,
  },
});

const emit = defineEmits(['switchView', 'itemClick']);
const { t } = useI18n();
const store = useStore();
const stageDotClass = color => getStageColorClasses(color).dot;
// Cor fora das 5 conhecidas não tem classe — sai por estilo inline.
const stageDotStyle = color => stageColorStyle(color, 'dot');

// Estado
const error = computed(() => store.state.pipelineCards.error);
const columns = ref([]);
const showAddModal = ref(false);
const selectedColumn = ref(null);
const addCardFormRef = ref(null);
const selectedPipeline = computed(
  () => store.getters['pipelines/getSelectedPipeline']
);
const { canViewPipeline, canManagePipeline } =
  usePipelinePermissions(selectedPipeline);
const showDeleteModal = ref(false);
const itemToDelete = ref(null);
const showEditModal = ref(false);
const itemToEdit = ref(null);
const editCardFormRef = ref(null);
const showDetailsModal = ref(false);
const itemToShow = ref(null);
// NOOVI: filtros do board vivem no store (aplicados server-side). Computed para
// manter UI e dados sempre em sincronia, mesmo após remount/troca de pipeline.
const activeFilters = computed(
  () => store.getters['pipelineCards/getActiveFilters'] || {}
);
const boardMeta = computed(() => store.getters['pipelineCards/getMeta'] || {});
const showFilterModal = ref(false);
const filteredResults = ref({ total: 0, stages: {} });
const isUpdating = ref(false);
const filterLoading = ref(false);
let filterTimer = null;
// Sequência de aplicação de filtros — invalida qualquer apply debounced que
// foi superado por um apply/clear mais novo (evita um timer antigo re-aplicar
// um filtro já removido).
let filterSeq = 0;
const searchQuery = ref(activeFilters.value.search || '');
const showCreatePipelineModal = ref(false);
const createPipelineFormRef = ref(null);

const cancelPendingFilterApply = () => {
  clearTimeout(filterTimer);
  filterTimer = null;
  filterSeq += 1;
  filterLoading.value = false;
};

const handleSwitchView = view => {
  // PipelineView waits for list/agenda data before replacing the keep-alive
  // component. Cancel here, before emitting, so a pending board debounce
  // cannot supersede that full-dataset request during the transition.
  cancelPendingFilterApply();
  emit('switchView', view);
};

const retryBoardLoad = async () => {
  try {
    await store.dispatch('pipelineCards/fetchBoardByStages');
  } catch {
    // The store owns the localized error state rendered below.
  }
};

// Lost Reason Modal State
const showLostReasonModal = ref(false);
const pendingLostMove = ref(null); // { itemId, columnId, cardTitle }

// Won Reason Modal State
const showWonModal = ref(false);
const wonCard = ref(null); // card inteiro: o modal precisa do funil e do histórico

// Hidden Columns State
const currentUserId = computed(() => store.getters.getCurrentUserID || 0);
const currentAccountId = computed(() => store.getters.getCurrentAccountId || 0);
let columnOrderGeneration = 0;
const normalizedActiveAccountId = () => {
  const accountId = store.getters.getCurrentAccountId;
  if (accountId === undefined || accountId === null) return null;
  return String(accountId).trim() || null;
};
const hiddenColumnIds = ref(new Set());

const getColumnSettingsKey = columnId =>
  `u${currentUserId.value}_a${currentAccountId.value}_pipeline_column_${columnId}_settings`;

const initHiddenColumns = columnIds => {
  const hidden = new Set();
  columnIds.forEach(id => {
    try {
      const raw = localStorage.getItem(getColumnSettingsKey(id));
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed.hideColumn === true) hidden.add(id);
      }
    } catch {
      // ignore corrupt localStorage entries
    }
  });
  hiddenColumnIds.value = hidden;
};

const handleColumnHideToggle = ({ id, hidden }) => {
  const next = new Set(hiddenColumnIds.value);
  if (hidden) {
    next.add(id);
  } else {
    next.delete(id);
  }
  hiddenColumnIds.value = next;
};

const showHiddenColumn = id => {
  try {
    const key = getColumnSettingsKey(id);
    const raw = localStorage.getItem(key);
    const settings = raw ? JSON.parse(raw) : {};
    settings.hideColumn = false;
    localStorage.setItem(key, JSON.stringify(settings));
  } catch {
    // ignore
  }
  handleColumnHideToggle({ id, hidden: false });
};

// Column Reorder State (B2.1 - UX/UI Sprint)
const draggingColumnId = ref(null);
const dragOverColumnId = ref(null);
const isReorderingColumns = ref(false);

// Scroll Indicators State (Phase 1 - UX Improvements)
const scrollContainer = ref(null);
let scrollResizeObserver = null;
const showLeftScrollIndicator = ref(false);
const showRightScrollIndicator = ref(false);
let wheelCleanup = null;
let wheelSnapTimer = null;
let wheelAccumDelta = 0;

// Onboarding Tour (B3 - UX/UI Sprint)
const tour = usePipelineTour();
let tourStartTimer = null;

// Start tour when pipeline loads for new users
onMounted(() => {
  // Delay tour start to ensure pipeline is fully loaded
  tourStartTimer = setTimeout(() => {
    if (
      tour.shouldShowTour.value &&
      !props.isLoading &&
      selectedPipeline.value
    ) {
      tour.startTour();
    }
  }, 1500);

  // Initialize scroll indicators (Phase 1 - UX Improvements)
  nextTick(() => {
    handleScroll(); // eslint-disable-line no-use-before-define
  });

  // Os indicadores de rolagem dependem de scrollWidth > clientWidth, e isso só
  // era recalculado no evento @scroll — ou seja, o botão que ensina que dá para
  // rolar só aparecia depois de rolar. Com etapas demais para caber, o usuário
  // não tinha nenhuma pista de que havia mais colunas (report 2026-08-05).
  // O observer recalcula na montagem, ao redimensionar a janela e quando
  // etapas são adicionadas ou removidas.
  if (typeof ResizeObserver !== 'undefined') {
    scrollResizeObserver = new ResizeObserver(() => {
      handleScroll(); // eslint-disable-line no-use-before-define
    });
    nextTick(() => {
      if (scrollContainer.value)
        scrollResizeObserver.observe(scrollContainer.value);
    });
  }
});

// Watch scrollContainer ref and add wheel listener when available
watch(
  scrollContainer,
  newContainer => {
    if (newContainer && !wheelCleanup) {
      const handleWheel = e => {
        // Se já está scrollando horizontalmente naturalmente, permitir
        if (e.deltaX !== 0) return;

        // Se o scroll vertical está disponível em modais/dropdowns, não converter
        if (e.target.closest('[role="dialog"]')) return;
        if (e.target.closest('.overflow-y-auto')) return;

        // Bug 4 (2026-04-22): handler precisa ser simétrico — antes só
        // calculava `canScrollRight`, deltaY negativo entrava no bloco
        // mesmo no início do scroll, prevent default sem feedback. Pior,
        // `wheelAccumDelta` não resetava ao inverter sentido, então o
        // snap timer arredondava com `Math.ceil` (avança pra direita)
        // mesmo quando o usuário queria voltar — daí a sensação de
        // "trava ao voltar pra esquerda". Agora ambos os lados são
        // checados; se o sinal inverte, accum reseta; e o snap usa
        // `Math.round` direção-neutro.
        const canScrollRight =
          newContainer.scrollLeft <
          newContainer.scrollWidth - newContainer.clientWidth - 5;
        const canScrollLeft = newContainer.scrollLeft > 5;
        const goingRight = e.deltaY > 0;
        const goingLeft = e.deltaY < 0;

        const blocked =
          (goingRight && !canScrollRight) || (goingLeft && !canScrollLeft);
        if (e.deltaY === 0 || blocked) return;

        e.preventDefault();
        e.stopPropagation();

        // Disable snap during wheel scroll to prevent snap-back
        newContainer.style.scrollSnapType = 'none';

        // Reset accumulator if direction inverted (evita snap pra direita
        // quando o usuário começa a voltar pra esquerda).
        if (
          (wheelAccumDelta > 0 && goingLeft) ||
          (wheelAccumDelta < 0 && goingRight)
        ) {
          wheelAccumDelta = 0;
        }
        wheelAccumDelta += e.deltaY;
        newContainer.scrollLeft += e.deltaY;
        handleScroll(); // eslint-disable-line no-use-before-define

        // After user stops scrolling, snap to nearest column
        clearTimeout(wheelSnapTimer);
        wheelSnapTimer = setTimeout(() => {
          // Largura/padding medidos no DOM: a coluna é elástica (256→384px),
          // qualquer constante aqui erra o alvo do snap.
          const columnWidth = getColumnStep(); // eslint-disable-line no-use-before-define
          const padding = getScrollPadding(); // eslint-disable-line no-use-before-define
          const pos = newContainer.scrollLeft - padding;
          // Round neutro: snap pra coluna mais próxima sem favorecer
          // a direção do último delta. Antes usava ceil/floor baseado
          // no sinal de wheelAccumDelta — quebrava ao inverter o sentido.
          const colIndex = Math.round(pos / columnWidth);
          const nearest = Math.max(0, colIndex) * columnWidth + padding;
          wheelAccumDelta = 0;
          // Scroll first (with snap still disabled), then restore snap
          newContainer.scrollTo({ left: nearest, behavior: 'smooth' });
          setTimeout(() => {
            newContainer.style.scrollSnapType = '';
          }, 400);
        }, 150);
      };

      newContainer.addEventListener('wheel', handleWheel, { passive: false });

      // Store cleanup function
      wheelCleanup = () => {
        newContainer.removeEventListener('wheel', handleWheel);
      };
    }
  },
  { immediate: true }
);

// Cleanup on unmount
onUnmounted(() => {
  scrollResizeObserver?.disconnect();
  scrollResizeObserver = null;

  if (wheelCleanup) {
    wheelCleanup();
  }
  stopEdgeAutoScroll(); // eslint-disable-line no-use-before-define
  clearTimeout(wheelSnapTimer);
  cancelPendingFilterApply();
  clearTimeout(tourStartTimer);
});

onDeactivated(cancelPendingFilterApply);

// Funções
const updateColumns = () => {
  // Obter os estágios do funil selecionado
  const pipeline = store.getters['pipelines/getSelectedPipeline'];
  if (!pipeline?.stages) return;

  // Verificar se etapas ganho/perdido estão habilitadas
  const showWonLost = pipeline.settings?.enable_won_lost_stages !== false;

  // Filtrar etapas de ganho/perdido se desabilitado
  const filteredEntries = Object.entries(pipeline.stages).filter(
    ([, stage]) => {
      if (!showWonLost) {
        return !stage.is_won_stage && !stage.is_lost_stage;
      }
      return true;
    }
  );

  // Transformar em array mantendo a ordem original do objeto
  const stagesArray = filteredEntries.map(([id, stage]) => ({
    id,
    title: stage.name || id, // Usa o id como fallback se stage.name for undefined
    color: stage.color,
    description: stage.description,
    position: parseInt(stage.position, 10) || 0,
    items: store.getters['pipelineCards/getPipelineCardItems'].filter(
      item =>
        item.pipeline_stage === id &&
        pipeline?.id &&
        item.pipeline_id &&
        Number(item.pipeline_id) === Number(pipeline.id)
    ),
  }));

  // P0.4 — logger.debug removido. As chamadas faziam .map(...) sobre
  // todos os stages a cada updateColumns, alocando arrays mesmo quando
  // o logger é no-op em produção.

  // Ordenar os estágios por posição
  stagesArray.sort((a, b) => a.position - b.position);

  // Atualizar as colunas com a ordem ordenada por posição
  columns.value = stagesArray;

  // Sync hidden column state from localStorage
  initHiddenColumns(stagesArray.map(s => s.id));
};

// Audit 2026-04-25 — watcher único (era duplicado).
// IMPORTANT: sem flush:'post'. Antes estava causando re-render duplo:
// uma vez do v-for diff de filteredColumns (com novo column.items),
// outra do post-watcher reescrevendo columns.value.
// Default flush:'pre' agrupa numa única passagem reativa.
watch(
  () => props.pipelineCards,
  () => {
    updateColumns();
  }
);

// Atualizar colunas quando o funil mudar
watch(
  selectedPipeline,
  newPipeline => {
    if (!newPipeline) return;

    // Garantir que as posições sejam números inteiros
    if (newPipeline.stages) {
      Object.values(newPipeline.stages).forEach(stage => {
        stage.position = parseInt(stage.position, 10) || 0;
      });
    }

    // Atualizar colunas com a ordenação correta
    updateColumns();
  },
  { immediate: true }
);

const handleAdd = columnId => {
  selectedColumn.value = columns.value.find(col => col.id === columnId);
  showAddModal.value = true;
};

const requestDelete = item => {
  if (!item || isUpdating.value) return;
  itemToDelete.value = item;
  showDeleteModal.value = true;
};

const closeDeleteModal = () => {
  if (isUpdating.value) return;
  showDeleteModal.value = false;
  itemToDelete.value = null;
};

const handleDelete = async itemToDeleteParam => {
  if (!itemToDeleteParam?.id || isUpdating.value) return;
  try {
    isUpdating.value = true;
    await store.dispatch(
      'pipelineCards/deletePipelineCardItem',
      itemToDeleteParam.id
    );
    showDeleteModal.value = false;
    itemToDelete.value = null;
  } catch (err) {
    emitter.emit('newToastMessage', {
      message: err.response?.data?.message || t('PIPELINE.ERROR_DELETING_ITEM'),
      action: { type: 'error' },
    });
  } finally {
    isUpdating.value = false;
  }
};

const handleDrop = async ({ itemId, columnId }) => {
  if (!itemId || !columnId) return;

  const pipeline = store.getters['pipelines/getSelectedPipeline'];
  const targetStage = pipeline?.stages?.[columnId];

  // Encontrar o card para mostrar informações no modal
  const cards = store.getters['pipelineCards/getPipelineCardItems'] || [];
  const card = cards.find(c => c.id === parseInt(itemId, 10));

  // Ignorar se o card já está na mesma coluna
  if (card && card.pipeline_stage === columnId) return;

  // Verificar se a coluna de destino é etapa de "Perdido"
  if (targetStage?.is_lost_stage) {
    pendingLostMove.value = {
      itemId: parseInt(itemId, 10),
      columnId,
      cardTitle: card?.item_details?.title || card?.title || 'Item',
    };
    showLostReasonModal.value = true;
    return; // Não move ainda, espera confirmação do modal
  }

  // Verificar se a coluna de destino é etapa de "Ganho".
  //
  // Arrastar para Ganho abre o MESMO modal do menu do card: fechar um negócio é
  // um gesto só, e ter dois modais fazia os dois caminhos divergirem — o do
  // arrasto ficou sem o catálogo de produtos (Fase 59/F3) e gravava toda venda
  // sem produto vinculado. `mark_as_won!` já move o card para a etapa de ganho
  // do funil, então não há `performDrop` aqui.
  if (targetStage?.is_won_stage) {
    wonCard.value = card;
    showWonModal.value = true;
    return; // Não move ainda, espera confirmação do modal
  }

  // Movimento normal para outras etapas
  await performDrop(itemId, columnId); // eslint-disable-line no-use-before-define
};

// Executa o movimento do card (chamado diretamente ou após confirmação do modal)
const performDrop = async (
  itemId,
  columnId,
  { lostReason = null, wonValue = null, wonNote = null } = {}
) => {
  try {
    isUpdating.value = true;

    // Chamada ao store - o store já gerencia a atualização dos itens
    await store.dispatch('pipelineCards/moveItemToStage', {
      itemId: parseInt(itemId, 10),
      columnId,
      lostReason,
      wonValue,
      wonNote,
    });

    // Apenas forçar atualização visual das colunas sem buscar novos dados
    nextTick(() => {
      updateColumns();
    });
  } catch (err) {
    // handlePipelineError (no store) já mostrou a mensagem específica — inclusive
    // a de "reabra o negócio". Repetir aqui dava DOIS toasts para o mesmo evento,
    // sendo o segundo o genérico "Erro ao mover item para etapa", que não diz
    // nada. Os casos específicos abaixo continuam valendo quando o handler não
    // teve o que mostrar.
    if (err?.pipelineToastShown) return;

    const errors = err.response?.data?.errors;
    let message =
      err.response?.data?.message || t('PIPELINE.ERROR_MOVING_ITEM');

    if (errors?.contact || errors?.contact_id) {
      message = t('PIPELINE.MOVE_ERROR_CONTACT_CROSS_TENANT');
    } else if (
      errors?.conversation_display ||
      errors?.conversation_display_id
    ) {
      message = t('PIPELINE.MOVE_ERROR_CONVERSATION_ORPHAN');
    }

    emitter.emit('newToastMessage', {
      message,
      action: { type: 'error' },
    });
  } finally {
    isUpdating.value = false;
  }
};

// Handler para confirmação do motivo de perda
const handleLostReasonConfirm = async reason => {
  showLostReasonModal.value = false;

  if (pendingLostMove.value) {
    const pending = pendingLostMove.value;
    pendingLostMove.value = null;
    await performDrop(pending.itemId, pending.columnId, {
      lostReason: reason,
    });
  }
};

// Handler para cancelamento do modal de motivo de perda
const handleLostReasonCancel = () => {
  showLostReasonModal.value = false;
  pendingLostMove.value = null;
};

// O servidor já fechou o card E o moveu para a etapa de ganho; aqui só
// aplicamos o resultado. O payload de deal_status é parcial (id, status,
// stage, won_*), então mesclamos sobre o card que já está no store em vez de
// substituí-lo — trocar pelo payload apagaria contato, etiquetas e o resto.
const handleWonSuccess = async payload => {
  showWonModal.value = false;
  const card = wonCard.value;
  wonCard.value = null;
  if (card && payload) {
    await store.dispatch('pipelineCards/replaceCard', { ...card, ...payload });
  }
  emitter.emit('pipeline-items-updated');
};

const handleWonCancel = () => {
  showWonModal.value = false;
  wonCard.value = null;
};

const handleSettings = () => {
  emitter.emit('newToastMessage', {
    message: t('PIPELINE.SETTINGS_COMING_SOON'),
    action: { type: 'info' },
  });
};

const handleItemCreated = async item => {
  if (!item) return;
  showAddModal.value = false;
  selectedColumn.value = null;
};

const filtersWithSearch = filters => {
  const nextFilters = { ...(filters || {}) };
  const search = searchQuery.value.trim();
  if (search) {
    nextFilters.search = search;
  } else {
    delete nextFilters.search;
  }
  return nextFilters;
};

const handleSearch = async query => {
  searchQuery.value = query;
  try {
    await store.dispatch(
      'pipelineCards/setActiveFilters',
      filtersWithSearch(activeFilters.value)
    );
  } catch {
    // O store mantém o erro localizado e o board oferece retry.
  }
};

const handleFilterClose = () => {
  showFilterModal.value = false;
};

const handleFilter = filters => {
  filterLoading.value = true;
  // Debounce — o usuário pode ajustar vários campos antes de aplicar.
  clearTimeout(filterTimer);
  filterSeq += 1;
  const seq = filterSeq;
  filterTimer = setTimeout(async () => {
    // Se um apply/clear mais novo já rodou, este timer está obsoleto.
    if (seq !== filterSeq) return;
    try {
      // Filtragem server-side: o backend devolve os cards já filtrados e o
      // watcher de props.pipelineCards reconstrói as colunas. Sem filtragem
      // em memória (que quebrava com a paginação por stage).
      await store.dispatch(
        'pipelineCards/setActiveFilters',
        filtersWithSearch(filters)
      );
    } finally {
      await nextTick();
      filterLoading.value = false;
    }
  }, 300);
};

const handleClearFilters = async () => {
  // Cancela qualquer apply debounced pendente E o invalida via sequência —
  // senão um timer antigo re-aplicaria os filtros 300ms depois de limpar.
  clearTimeout(filterTimer);
  filterSeq += 1;
  showFilterModal.value = false;
  filterLoading.value = true;
  try {
    // Limpar o modal não apaga a busca independente que continua visível no
    // header; só os demais filtros são removidos.
    await store.dispatch(
      'pipelineCards/setActiveFilters',
      filtersWithSearch({})
    );
  } finally {
    await nextTick();
    filterLoading.value = false;
  }
};

const handleEdit = item => {
  itemToEdit.value = item;
  showEditModal.value = true;
};

const handleItemClick = item => {
  emit('itemClick', item);
};

const handleCardStageChanged = async ({ card }) => {
  // Replace entire card in store with updated data from API
  await store.dispatch('pipelineCards/replaceCard', card);

  // Update columns after store is updated
  await nextTick();
  updateColumns();
};

const handleEditFromDetails = item => {
  showDetailsModal.value = false;
  handleEdit(item);
};

const handleDetailsUpdate = () => {
  // No need to emit 'forceUpdate' here
};

const handleItemEdited = async item => {
  if (!item) return;
  showEditModal.value = false;
  itemToEdit.value = null;

  // Forçar atualização visual das colunas sem buscar novos dados
  nextTick(() => {
    updateColumns();
  });
};

// Handler for PipelineForm save — store already updated by PipelineForm's dispatch
const handlePipelineCreated = savedPipeline => {
  if (savedPipeline) {
    const pipelineId = savedPipeline.id || savedPipeline;
    store.dispatch('pipelines/setSelectedPipeline', {
      id: String(pipelineId),
      ...savedPipeline,
    });
  }
  showCreatePipelineModal.value = false;
};

const requestAddCardModalClose = () =>
  addCardFormRef.value?.requestClose?.() ?? true;
const requestEditCardModalClose = () =>
  editCardFormRef.value?.requestClose?.() ?? true;
const requestCreatePipelineModalClose = () =>
  createPipelineFormRef.value?.requestClose?.() ?? true;

// ========================================
// COLUMN REORDER - B2.1 UX/UI Sprint
// Drag & Drop to reorder pipeline stages
// ========================================

const handleColumnDragStart = (event, columnId) => {
  if (!canManagePipeline.value) {
    event.preventDefault();
    return;
  }
  // Allow card drag to proceed without interference
  // Cards handle their own drag events
  if (event.target.closest('.pipeline-card')) {
    // Don't prevent default - let the card handle its own drag
    // Just don't set column drag state
    return;
  }

  if (
    event.target.closest(
      'button, input, select, textarea, a, [role="menuitem"]'
    )
  ) {
    event.preventDefault();
    return;
  }

  // Only start column drag if clicking on column header or empty area
  draggingColumnId.value = columnId;
  event.dataTransfer.effectAllowed = 'move';
  event.dataTransfer.setData('column-id', columnId);
};

const handleColumnDragEnd = () => {
  draggingColumnId.value = null;
  dragOverColumnId.value = null;
};

const handleColumnDragOver = (event, columnId) => {
  event.preventDefault();

  // Only allow column drag, not card drag
  if (!draggingColumnId.value || draggingColumnId.value === columnId) {
    return;
  }

  event.dataTransfer.dropEffect = 'move';
  dragOverColumnId.value = columnId;
};

const handleColumnDragEnter = (event, columnId) => {
  if (!draggingColumnId.value || draggingColumnId.value === columnId) {
    return;
  }

  dragOverColumnId.value = columnId;
};

const handleColumnDragLeave = event => {
  // Only clear if actually leaving the column area
  const relatedTarget = event.relatedTarget;
  if (!event.currentTarget.contains(relatedTarget)) {
    dragOverColumnId.value = null;
  }
};

const handleColumnDrop = async (event, targetColumnId) => {
  event.preventDefault();
  if (!canManagePipeline.value) return;

  const sourceColumnId = draggingColumnId.value;

  // Reset drag state
  handleColumnDragEnd();

  if (!sourceColumnId || sourceColumnId === targetColumnId) {
    return;
  }

  // Find source and target indices
  const sourceIndex = columns.value.findIndex(col => col.id === sourceColumnId);
  const targetIndex = columns.value.findIndex(col => col.id === targetColumnId);

  if (sourceIndex === -1 || targetIndex === -1) {
    return;
  }

  const reorderedColumns = movePipelineColumn(
    columns.value,
    sourceColumnId,
    targetIndex
  );
  if (!reorderedColumns) return;

  columns.value = reorderedColumns;

  // Persist to backend
  await persistColumnOrder(reorderedColumns); // eslint-disable-line no-use-before-define
};

const persistColumnOrder = async reorderedColumns => {
  const pipeline = store.getters['pipelines/getSelectedPipeline'];
  if (!pipeline?.id || !canManagePipeline.value) return;
  const accountId = normalizedActiveAccountId();
  if (accountId === null) return;
  columnOrderGeneration += 1;
  const requestGeneration = columnOrderGeneration;
  const pipelineId = pipeline.id;
  const requestIsCurrent = () =>
    requestGeneration === columnOrderGeneration &&
    accountId === normalizedActiveAccountId() &&
    String(pipelineId) ===
      String(store.getters['pipelines/getSelectedPipeline']?.id ?? '');

  try {
    isReorderingColumns.value = true;

    // Build updated stages object with new positions
    const updatedStages = {};
    reorderedColumns.forEach((col, index) => {
      updatedStages[col.id] = {
        ...pipeline.stages[col.id],
        position: index,
      };
    });

    // Update via API
    await PipelinesAPI.update(pipelineId, { stages: updatedStages }, accountId);
    if (!requestIsCurrent()) return;

    // Refresh pipeline in store
    await store.dispatch('pipelines/fetch', {
      force: true,
      accountId,
      generation: requestGeneration,
    });
    if (!requestIsCurrent()) return;

    emitter.emit('newToastMessage', {
      message: t('PIPELINE.COLUMN_REORDER.SUCCESS'),
      action: { type: 'success' },
    });
  } catch (err) {
    if (!requestIsCurrent()) return;
    logger.error('Error reordering columns:', err);

    // Revert to original order
    updateColumns();

    emitter.emit('newToastMessage', {
      message: t('PIPELINE.COLUMN_REORDER.ERROR'),
      action: { type: 'error' },
    });
  } finally {
    if (requestIsCurrent()) isReorderingColumns.value = false;
  }
};

watch(
  currentAccountId,
  (accountId, previousAccountId) => {
    if (String(accountId ?? '') === String(previousAccountId ?? '')) return;
    columnOrderGeneration += 1;
    isReorderingColumns.value = false;
  },
  { flush: 'sync' }
);

const canMoveColumn = (columnId, direction) => {
  if (!canManagePipeline.value) return false;
  const currentIndex = columns.value.findIndex(col => col.id === columnId);
  const targetIndex = currentIndex + direction;
  return (
    currentIndex !== -1 &&
    targetIndex >= 0 &&
    targetIndex < columns.value.length
  );
};

const handleColumnReorder = async ({ columnId, direction }) => {
  if (
    !canManagePipeline.value ||
    isReorderingColumns.value ||
    !canMoveColumn(columnId, direction)
  ) {
    return;
  }

  const sourceIndex = columns.value.findIndex(col => col.id === columnId);
  const reorderedColumns = movePipelineColumn(
    columns.value,
    columnId,
    sourceIndex + direction
  );
  if (!reorderedColumns) return;

  columns.value = reorderedColumns;
  await persistColumnOrder(reorderedColumns);
};

// Scroll Indicators Handler (Phase 1 - UX Improvements)
const handleScroll = () => {
  if (!scrollContainer.value) return;

  const { scrollLeft, scrollWidth, clientWidth } = scrollContainer.value;
  // Limiar de 8px: a folga antiga de 30px escondia a seta esquerda durante
  // quase toda a primeira coluna, e o usuário perdia a pista de que dava para
  // voltar. 8px só absorve o arredondamento sub-pixel do snap.
  showLeftScrollIndicator.value = scrollLeft > 8;
  showRightScrollIndicator.value = scrollLeft < scrollWidth - clientWidth - 8;
};

// Passo de uma etapa, MEDIDO no DOM.
// As constantes antigas (coluna 288 + gap 16 = 304, padding 24) descreviam um
// layout que não existe mais: hoje a coluna é elástica (min 256 / max 384,
// gap 8, padding 16), então o passo fixo desalinhava o snap um pouco mais a
// cada clique. Uma única fonte de verdade para as setas e para o snap da roda.
const getColumnStep = () => {
  const container = scrollContainer.value;
  if (!container) return 264;
  const column = container.querySelector(':scope > div');
  const gap = parseFloat(getComputedStyle(container).columnGap) || 0;
  const width = column?.getBoundingClientRect().width || 0;
  return width > 0 ? width + gap : 264;
};

const getScrollPadding = () => {
  const container = scrollContainer.value;
  if (!container) return 16;
  return parseFloat(getComputedStyle(container).paddingLeft) || 0;
};

const scrollRightFn = () => {
  if (!scrollContainer.value) return;
  scrollContainer.value.scrollBy({ left: getColumnStep(), behavior: 'smooth' });
};

const scrollLeftFn = () => {
  if (!scrollContainer.value) return;
  scrollContainer.value.scrollBy({
    left: -getColumnStep(),
    behavior: 'smooth',
  });
};

// Navegação por teclado no board: com o foco no grupo de etapas, ←/→ andam
// uma coluna. Antes só existia Tab, que percorre card a card.
const handleBoardKeydown = event => {
  if (event.altKey || event.ctrlKey || event.metaKey) return;
  // Só quando o próprio board está focado. Agir em keydown borbulhado dos
  // descendentes sequestraria as setas de qualquer campo, menu, listbox ou
  // widget ARIA dentro dos cards — a lista de exceções nunca fecharia.
  if (event.target !== event.currentTarget) return;

  if (event.key === 'ArrowRight') {
    event.preventDefault();
    scrollRightFn();
  } else if (event.key === 'ArrowLeft') {
    event.preventDefault();
    scrollLeftFn();
  }
};

// Auto-scroll ao arrastar um card (ou uma etapa) até a borda do board.
// Sem isso não há como mover um card para uma etapa fora da viewport: o drag
// nativo do HTML5 não emite eventos de mouse, então a única pista de posição
// durante o arrasto é o `dragover`. O loop roda em rAF porque o `dragover`
// para de disparar quando o ponteiro fica parado sobre a borda — que é
// exatamente o gesto de "encostar na borda e esperar".
const EDGE_SCROLL_ZONE = 72;
const EDGE_SCROLL_MIN_SPEED = 6;
const EDGE_SCROLL_MAX_SPEED = 26;
let edgeScrollFrame = null;
let edgeScrollVelocity = 0;

const stopEdgeAutoScroll = () => {
  edgeScrollVelocity = 0;
  if (edgeScrollFrame !== null) {
    cancelAnimationFrame(edgeScrollFrame);
    edgeScrollFrame = null;
  }
  if (scrollContainer.value) {
    scrollContainer.value.style.scrollSnapType = '';
  }
};

// `dragleave` borbulha a cada troca de elemento sob o ponteiro — parar o
// auto-scroll nele cru faria o board engasgar ao atravessar os cards. Só
// interessa a saída real do container.
const handleBoardDragLeave = event => {
  if (event.currentTarget.contains(event.relatedTarget)) return;
  stopEdgeAutoScroll();
};

const runEdgeAutoScroll = () => {
  const container = scrollContainer.value;
  if (!container || edgeScrollVelocity === 0) {
    stopEdgeAutoScroll();
    return;
  }
  const before = container.scrollLeft;
  container.scrollLeft += edgeScrollVelocity;
  // Bateu na ponta do board: parar aqui em vez de reagendar um frame que não
  // move mais nada (um drag cancelado com Esc não emite `drop`).
  if (container.scrollLeft === before) {
    stopEdgeAutoScroll();
    return;
  }
  handleScroll();
  edgeScrollFrame = requestAnimationFrame(runEdgeAutoScroll);
};

const handleBoardDragOver = event => {
  const container = scrollContainer.value;
  if (!container) return;

  const { left, right } = container.getBoundingClientRect();
  const distanceFromLeft = event.clientX - left;
  const distanceFromRight = right - event.clientX;

  // Intensidade proporcional à profundidade dentro da zona: encostar de leve
  // arrasta devagar, colar na borda acelera.
  const speedFor = distance => {
    const ratio = Math.min(1, Math.max(0, 1 - distance / EDGE_SCROLL_ZONE));
    return (
      EDGE_SCROLL_MIN_SPEED +
      ratio * (EDGE_SCROLL_MAX_SPEED - EDGE_SCROLL_MIN_SPEED)
    );
  };

  const canScrollLeft = container.scrollLeft > 0;
  const canScrollRight =
    container.scrollLeft < container.scrollWidth - container.clientWidth - 1;

  if (distanceFromLeft < EDGE_SCROLL_ZONE && canScrollLeft) {
    edgeScrollVelocity = -speedFor(distanceFromLeft);
  } else if (distanceFromRight < EDGE_SCROLL_ZONE && canScrollRight) {
    edgeScrollVelocity = speedFor(distanceFromRight);
  } else {
    stopEdgeAutoScroll();
    return;
  }

  // O snap brigaria com o scroll contínuo, puxando de volta a cada frame.
  container.style.scrollSnapType = 'none';
  if (edgeScrollFrame === null) {
    edgeScrollFrame = requestAnimationFrame(runEdgeAutoScroll);
  }
};

// Os cards já chegam filtrados pelo servidor. Esta computed só controla quais
// colunas ficam visíveis e preserva a ordenação do pipeline.
const filteredColumns = computed(() => {
  // Criar uma cópia exata para manter a mesma ordem
  let result = [...columns.value];
  // P0.4 — logger.debug com .map() removido (alocava array a cada re-eval).

  // Filter by stages if selected in global filter
  if (activeFilters.value?.stages?.length > 0) {
    result = result.filter(column =>
      activeFilters.value.stages.includes(column.id)
    );
  }

  // NOOVI: filtragem de cards (label/prioridade/valor/agente/data/status/SLA)
  // agora é server-side — o backend devolve `items` já filtrado. Aqui só
  // ocultamos colunas fora dos stages selecionados (acima).
  // O bloco de filtragem em memória foi removido: quebrava com a paginação por
  // stage (filtrava só a página carregada) e tinha chaves dessincronizadas.

  // Ordenar explicitamente por posição antes de retornar, garantindo ordem correta
  result.sort((a, b) => {
    const posA = parseInt(a.position, 10) || 0;
    const posB = parseInt(b.position, 10) || 0;
    return posA - posB;
  });

  // P0.4 — logger.debug com .map() removido (alocava array a cada re-eval).
  return result;
});

// O backend não expõe agregado monetário. Enquanto qualquer coluna ainda
// tiver páginas no servidor, o total calculado com os cards carregados seria
// parcial e não deve ser apresentado como total do pipeline.
const boardHasPartialDataset = computed(() => {
  const stagePagination = store.getters['pipelineCards/getCardsByStage'] || {};
  const stageCounts = boardMeta.value.stage_counts || {};

  return filteredColumns.value.some(column => {
    const pagination = stagePagination[column.id];
    if (pagination?.hasMore) return true;

    const serverCount = Number(stageCounts[column.id]);
    return Number.isFinite(serverCount) && serverCount > column.items.length;
  });
});

// Computed para detectar se há filtros ativos sem resultados
const hasFiltersWithNoResults = computed(() => {
  const f = activeFilters.value;
  if (!f) return false;

  // Verificar se há algum filtro ativo (formato aninhado do PipelineFilter).
  // value.min/max usam checagem explícita para não ignorar o valor 0.
  const isSet = v => v !== null && v !== undefined && v !== '';
  const hasActiveFilters = Boolean(
    f.search?.trim() ||
      f.labels?.length ||
      f.priority?.length ||
      f.stages?.length ||
      isSet(f.value?.min) ||
      isSet(f.value?.max) ||
      f.agent_id ||
      f.date?.start ||
      f.date?.end ||
      f.status ||
      f.sla_exceeded
  );

  if (!hasActiveFilters) return false;

  // Verificar se todas as colunas estão vazias
  const totalItems = filteredColumns.value.reduce(
    (sum, column) => sum + column.items.length,
    0
  );

  return totalItems === 0;
});

// Computed para calcular os resultados da busca
const searchResults = computed(() => {
  if (!searchQuery.value) return { total: 0, stages: {} };

  const results = {
    total: 0,
    stages: {},
  };

  const stageCounts = boardMeta.value.stage_counts || {};
  filteredColumns.value.forEach(column => {
    const count = Number(stageCounts[column.id] ?? column.items.length) || 0;
    if (count > 0) {
      results.stages[column.title] = count;
      results.total += count;
    }
  });

  return results;
});

const hiddenColumns = computed(() =>
  filteredColumns.value.filter(col => hiddenColumnIds.value.has(col.id))
);

// P0.4 — columnsOrderDebug removido. Era um computed inteiro que existia
// apenas para chamar logger.debug com .map() das colunas. Não tinha
// uso real além de log; o template referenciava num <div hidden>.

// Watch for column changes to update scroll indicators (Phase 1 - UX Improvements)
watch(
  () => filteredColumns.value,
  () => {
    nextTick(() => {
      handleScroll();
    });
  },
  { flush: 'post' }
);
</script>

<template>
  <!-- `w-full flex-1`: este contêiner é item de um flex row (o shell do
       dashboard) e não declarava crescimento, então era dimensionado pelo
       conteúdo e parava ~135px antes da borda numa tela de 1920 — o board
       inteiro ficava com uma faixa morta à direita, independente da quantidade
       de etapas (report 2026-08-06). -->
  <div
    class="flex w-full flex-1 flex-col h-full min-w-0 bg-gradient-to-br from-n-background to-n-solid-2"
  >
    <!-- Access denied: agent is not a member of this pipeline -->
    <div
      v-if="!canViewPipeline"
      class="flex flex-1 items-center justify-center p-8"
    >
      <div class="text-center max-w-sm">
        <fluent-icon
          icon="lock-closed"
          size="40"
          class="mx-auto mb-4 text-n-slate-9"
        />
        <h3 class="text-heading-2 text-n-slate-12 mb-2">
          {{ t('PIPELINE.ACCESS_DENIED.TITLE') }}
        </h3>
        <p class="text-body-main text-n-slate-10">
          {{ t('PIPELINE.ACCESS_DENIED.DESCRIPTION') }}
        </p>
      </div>
    </div>

    <template v-else>
      <header :aria-label="t('PIPELINE.HEADER')">
        <PipelineHeader
          :current-view="currentView"
          :current-stage="selectedColumn?.id"
          :search-results="searchResults"
          :search-value="searchQuery"
          :columns="filteredColumns"
          :active-filters="activeFilters"
          :pipeline-cards="pipelineCards"
          :has-partial-dataset="boardHasPartialDataset"
          @filter="showFilterModal = true"
          @settings="handleSettings"
          @item-created="handleItemCreated"
          @items-updated="
            () => store.dispatch('pipelineCards/fetchBoardByStages')
          "
          @search="handleSearch"
          @switch-view="handleSwitchView"
          @start-tour="
            tour.resetTour();
            tour.startTour();
          "
        />
      </header>

      <!-- Skeleton Loading State -->
      <div v-if="isLoading" class="flex-1 overflow-hidden">
        <PipelineBoardSkeleton :columns="4" :cards-per-column="[3, 2, 4, 2]" />
      </div>

      <div
        v-else-if="error"
        role="alert"
        class="flex flex-1 flex-col items-center justify-center gap-3 p-6 text-center text-n-ruby-11"
      >
        <p>{{ error }}</p>
        <button
          type="button"
          class="min-h-11 rounded-lg border border-n-weak px-4 text-sm font-medium text-n-slate-12 hover:bg-n-alpha-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-brand"
          @click="retryBoardLoad"
        >
          {{ t('PIPELINE.MESSAGES.RETRY') }}
        </button>
      </div>

      <div
        v-else-if="!selectedPipeline && !isLoading && !error"
        class="flex flex-col items-center justify-center flex-1 p-8 text-center"
      >
        <h3 class="text-heading-1 text-n-slate-12 mb-2">
          {{ t('PIPELINE.PIPELINES.FORM.CREATE_FIRST') }}
        </h3>
        <p class="text-body-main text-n-slate-11 max-w-md mb-6">
          {{ t('PIPELINE.PIPELINES.FORM.CREATE_FIRST_MESSAGE') }}
        </p>
        <Button
          v-if="canManagePipeline"
          variant="solid"
          color-scheme="woot"
          icon="add"
          :label="t('PIPELINE.PIPELINES.FORM.CREATE_BUTTON')"
          @click="showCreatePipelineModal = true"
        />
      </div>

      <!-- `min-h-0 overflow-hidden` (2026-08-06): sem isso o `min-height:auto`
           do flexbox impedia este <main> de encolher abaixo do conteúdo — ele
           media 8050px numa viewport de 1052px, e toda a cadeia `h-full`
           abaixo herdava a altura inflada. Consequência dupla: o
           `overflow-y-auto` da coluna nunca ativava (clientHeight ===
           scrollHeight, nada para rolar, cards decapitados pelo
           `overflow-hidden` do shell) e o chevron das setas laterais,
           centralizado nos 8050px, ficava em y≈4082 — fora da tela.
           Uma classe, os dois sintomas. -->
      <!-- O ciclo de drag fica AQUI, não no container de rolagem: os chips de
           navegação são irmãos sobrepostos dentro da faixa de auto-scroll, e
           arrastar um card por cima de um deles disparava `dragleave` no
           scroller sem que o `dragover` do chip chegasse até ele — 40px mortos
           bem no meio da zona de 72px. O <main> contém os dois. -->
      <main
        v-else
        :aria-label="t('PIPELINE.BOARD')"
        class="relative flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden"
        @dragover="handleBoardDragOver"
        @dragleave="handleBoardDragLeave"
        @drop="stopEdgeAutoScroll"
        @dragend="stopEdgeAutoScroll"
      >
        <!-- Left Scroll Indicator.
             Antes era UM botão de 48px cobrindo `top-0 bottom-0`: com o <main>
             inflado ele virava uma área clicável de 48×8050px que engolia
             clique e drop na borda do board, enquanto o próprio chevron ficava
             centralizado fora da tela. Agora são duas peças: a faixa de
             gradiente é puramente decorativa (`pointer-events-none`) e o
             controle é um chip ancorado no meio da altura VISÍVEL. -->
        <div
          v-if="showLeftScrollIndicator"
          aria-hidden="true"
          class="pointer-events-none absolute inset-y-0 left-0 z-10 w-12 bg-gradient-to-r from-n-background to-transparent"
        />
        <button
          v-if="showLeftScrollIndicator"
          type="button"
          :aria-label="t('PIPELINE.BOARD_SCROLL_PREVIOUS')"
          class="absolute left-2 top-1/2 z-20 flex size-10 -translate-y-1/2 items-center justify-center rounded-full bg-n-solid-3 text-n-slate-11 shadow-md outline outline-1 outline-n-weak transition-colors hover:bg-n-alpha-2 hover:text-n-slate-12 focus-visible:outline-2 focus-visible:outline-n-brand"
          @click="scrollLeftFn"
        >
          <fluent-icon icon="chevron-left" size="20" />
        </button>

        <!-- Pipeline Columns Container.
             `flex flex-col` em vez de bloco com `h-full` no filho: os banners
             (filtro sem resultado, etapas ocultas) são irmãos do container de
             rolagem, então precisam DESCONTAR altura dele. Com o
             `h-full` antigo o container pedia 100% do pai mesmo com banner
             aberto e a soma estourava. -->
        <div class="flex min-h-0 min-w-0 flex-1 flex-col">
          <div
            v-if="isUpdating"
            class="absolute top-2 right-2 flex items-center text-body-main text-n-slate-10"
          >
            <span
              aria-hidden="true"
              class="mr-2 size-4 animate-spin rounded-full border-2 border-n-weak border-t-n-brand motion-reduce:animate-none"
            />
            {{ t('PIPELINE.UPDATING') }}
          </div>

          <div
            v-if="filterLoading"
            class="absolute top-2 left-1/2 transform -translate-x-1/2 flex items-center px-3 py-2 bg-n-solid-3 rounded-xl shadow-md outline outline-1 outline-n-weak text-body-main text-n-slate-11"
          >
            <span
              aria-hidden="true"
              class="mr-2 size-4 animate-spin rounded-full border-2 border-n-weak border-t-n-brand motion-reduce:animate-none"
            />
            {{ t('PIPELINE.FILTERS.APPLYING') }}
          </div>

          <!-- Compact banner for filters with no results - shown above columns -->
          <div
            v-if="hasFiltersWithNoResults"
            class="mx-6 mt-4 mb-2 p-3 bg-n-solid-2 border border-n-weak rounded-xl flex items-center justify-between"
          >
            <div class="flex items-center gap-3">
              <fluent-icon icon="filter" size="20" class="text-n-slate-9" />
              <div>
                <p class="text-body-main font-medium text-n-slate-12">
                  {{ t('PIPELINE.FILTERS.NO_RESULTS.TITLE') }}
                </p>
                <p class="text-label-small text-n-slate-10">
                  {{ t('PIPELINE.FILTERS.NO_RESULTS.DESCRIPTION') }}
                </p>
              </div>
            </div>
            <Button
              data-test="clear-pipeline-filters"
              variant="solid"
              color="slate"
              size="sm"
              class="min-h-11"
              @click="handleClearFilters"
            >
              <template #icon>
                <fluent-icon icon="filter" size="14" />
              </template>
              {{ t('PIPELINE.FILTERS.NO_RESULTS.CLEAR_BUTTON') }}
            </Button>
          </div>

          <!-- Hidden Stages Banner -->
          <div
            v-if="hiddenColumns.length > 0"
            class="mx-6 mt-2 mb-1 p-3 bg-n-solid-2 border border-n-weak rounded-xl flex items-center gap-3 flex-wrap"
          >
            <div class="flex items-center gap-2 text-n-slate-10 shrink-0">
              <fluent-icon icon="eye-off" size="16" />
              <span class="text-label-small font-medium">{{
                t('PIPELINE.HIDDEN_STAGES.LABEL')
              }}</span>
            </div>
            <div class="flex items-center gap-2 flex-wrap">
              <button
                v-for="col in hiddenColumns"
                :key="col.id"
                type="button"
                class="flex min-h-11 items-center gap-1.5 rounded-lg border border-n-weak bg-n-solid-3 px-2.5 py-1 text-label-small font-medium text-n-slate-11 transition-colors hover:bg-n-alpha-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-blue-9"
                @click="showHiddenColumn(col.id)"
              >
                <span
                  class="w-2 h-2 rounded-full shrink-0"
                  :class="stageDotClass(col.color)"
                  :style="stageDotStyle(col.color)"
                />
                {{ col.title }}
                <fluent-icon icon="eye" size="12" class="text-n-slate-9" />
              </button>
            </div>
          </div>

          <div
            ref="scrollContainer"
            role="group"
            tabindex="0"
            :aria-label="t('PIPELINE.STAGES')"
            class="flex min-h-0 flex-1 snap-x snap-proximity gap-2 overflow-x-auto px-4 py-3 scroll-pl-4 scroll-pr-4 [scrollbar-color:rgb(148_163_184/0.3)_transparent] [scrollbar-width:thin] [&::-webkit-scrollbar]:h-1.5 [&::-webkit-scrollbar-thumb]:rounded-[3px] [&::-webkit-scrollbar-thumb]:bg-slate-400/30 hover:[&::-webkit-scrollbar-thumb]:bg-slate-400/50 [&::-webkit-scrollbar-track]:bg-transparent"
            @scroll="handleScroll"
            @keydown="handleBoardKeydown"
          >
            <!-- P0.4 — Debug helper removido (era apenas para chamar logger). -->
            <!-- Column Reorder Wrapper (B2.1 UX/UI Sprint).
                 P0.4 — sort redundante removido do template (.slice().sort()
                 era extra; filteredColumns já ordena por posição internamente).

                 Largura (2026-08-06): é ESTE wrapper que manda, não a section
                 dentro de PipelineColumn. O commit 6b53618466 pôs `flex-1` na
                 section para acabar com a sobra à direita em funis curtos, mas
                 aqui continuava `w-72 min-w-[18rem] max-w-[18rem]` — o teto do
                 pai anulava o crescimento e o vão permanecia.
                 Agora: piso de 256px, cresce até 384px para ocupar a faixa
                 quando há poucas etapas, e a partir de ~6 etapas volta ao piso
                 com a rolagem horizontal do container assumindo. -->
            <template v-for="column in filteredColumns" :key="column.id">
              <div
                v-if="!hiddenColumnIds.has(column.id)"
                class="relative h-full flex-1 min-w-64 max-w-96 snap-start transition-[transform,opacity] duration-150 motion-reduce:transition-none"
                :class="{
                  'cursor-grab active:cursor-grabbing': canManagePipeline,
                  'z-[100] rotate-2 scale-[1.02] opacity-50 shadow-xl motion-reduce:transform-none':
                    draggingColumnId === column.id,
                  'scale-[0.98] rounded-xl shadow-[inset_0_0_0_3px_#3b82f6,0_4px_6px_-1px_rgb(0_0_0/0.1)] dark:shadow-[inset_0_0_0_3px_#60a5fa,0_4px_6px_-1px_rgb(0_0_0/0.3)] motion-reduce:transform-none':
                    dragOverColumnId === column.id &&
                    draggingColumnId !== column.id,
                }"
                :draggable="canManagePipeline"
                @dragstart="e => handleColumnDragStart(e, column.id)"
                @dragend="handleColumnDragEnd"
                @dragover="e => handleColumnDragOver(e, column.id)"
                @dragenter="e => handleColumnDragEnter(e, column.id)"
                @dragleave="handleColumnDragLeave"
                @drop="e => handleColumnDrop(e, column.id)"
              >
                <PipelineColumn
                  :id="column.id"
                  :title="column.title"
                  :color="column.color"
                  :description="column.description"
                  :items="column.items"
                  :can-move-previous="canMoveColumn(column.id, -1)"
                  :can-move-next="canMoveColumn(column.id, 1)"
                  @add="handleAdd"
                  @edit="handleEdit"
                  @delete="requestDelete"
                  @drop="handleDrop"
                  @item-click="handleItemClick"
                  @card-stage-changed="handleCardStageChanged"
                  @hide-toggle="handleColumnHideToggle"
                  @reorder="handleColumnReorder"
                />
              </div>
            </template>
          </div>

          <!-- Right Scroll Indicator (mesma separação faixa/chip da esquerda) -->
          <div
            v-if="showRightScrollIndicator"
            aria-hidden="true"
            class="pointer-events-none absolute inset-y-0 right-0 z-10 w-12 bg-gradient-to-l from-n-background to-transparent"
          />
          <button
            v-if="showRightScrollIndicator"
            type="button"
            :aria-label="t('PIPELINE.BOARD_SCROLL_NEXT')"
            class="absolute right-2 top-1/2 z-20 flex size-10 -translate-y-1/2 items-center justify-center rounded-full bg-n-solid-3 text-n-slate-11 shadow-md outline outline-1 outline-n-weak transition-colors hover:bg-n-alpha-2 hover:text-n-slate-12 focus-visible:outline-2 focus-visible:outline-n-brand"
            @click="scrollRightFn"
          >
            <fluent-icon icon="chevron-right" size="20" />
          </button>
        </div>
      </main>

      <!-- Modais -->
      <Modal
        v-model:show="showAddModal"
        :before-close="requestAddCardModalClose"
        @close="() => (showAddModal = false)"
      >
        <div class="flex flex-col max-h-[85vh] overflow-hidden">
          <div class="px-5 pt-5 pb-3">
            <h3 class="text-sm font-semibold text-n-slate-12">
              {{ t('PIPELINE.ADD_ITEM') }}
            </h3>
          </div>
          <PipelineCardForm
            v-if="selectedColumn && selectedPipeline"
            ref="addCardFormRef"
            :pipeline-id="selectedPipeline.id"
            :stage="selectedColumn.id"
            :position="selectedColumn.position"
            @saved="handleItemCreated"
            @close="showAddModal = false"
          />
          <div v-else class="text-center text-red-500 p-6">
            {{ t('PIPELINE.ERRORS.NO_PIPELINE_SELECTED') }}
          </div>
        </div>
      </Modal>

      <Modal v-model:show="showDeleteModal" @close="closeDeleteModal">
        <div class="p-6">
          <h3 class="text-lg font-medium mb-4">
            {{ t('PIPELINE.DELETE_CONFIRMATION.TITLE') }}
          </h3>
          <p class="text-body-main text-n-slate-11 mb-6">
            {{
              t('PIPELINE.DELETE_CONFIRMATION.MESSAGE', {
                item:
                  itemToDelete?.title ||
                  itemToDelete?.item_details?.title ||
                  t('PIPELINE.NO_TITLE'),
              })
            }}
          </p>
          <div class="flex justify-end gap-2">
            <Button
              variant="ghost"
              color="slate"
              size="md"
              :disabled="isUpdating"
              @click="closeDeleteModal"
            >
              {{ t('PIPELINE.CANCEL') }}
            </Button>
            <Button
              data-test="confirm-pipeline-card-delete"
              variant="solid"
              color="ruby"
              size="md"
              :is-loading="isUpdating"
              :disabled="isUpdating || !itemToDelete"
              @click="handleDelete(itemToDelete)"
            >
              {{ t('PIPELINE.DELETE') }}
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        v-model:show="showEditModal"
        :before-close="requestEditCardModalClose"
        @close="
          () => {
            showEditModal = false;
            itemToEdit = null;
          }
        "
      >
        <div class="flex flex-col max-h-[85vh] overflow-hidden">
          <div class="px-5 pt-5 pb-3">
            <h3 class="text-sm font-semibold text-n-slate-12">
              {{ t('PIPELINE.FORM.EDIT_ITEM') }}
            </h3>
          </div>
          <PipelineCardForm
            v-if="itemToEdit && selectedPipeline"
            ref="editCardFormRef"
            :pipeline-id="selectedPipeline.id"
            :stage="itemToEdit.pipeline_stage"
            :position="itemToEdit.position"
            :initial-data="itemToEdit"
            is-editing
            @saved="handleItemEdited"
            @close="showEditModal = false"
          />
        </div>
      </Modal>

      <Modal
        v-model:show="showDetailsModal"
        size="full-width"
        @close="
          () => {
            showDetailsModal = false;
            itemToShow = null;
            handleDetailsUpdate();
          }
        "
      >
        <PipelineCardDetails
          v-if="itemToShow"
          :key="itemToShow.id"
          :item="itemToShow"
          @close="
            () => {
              showDetailsModal = false;
              itemToShow = null;
              handleDetailsUpdate();
            }
          "
          @edit="handleEditFromDetails"
          @item-updated="handleDetailsUpdate"
        />
      </Modal>

      <!-- Modal for Creating Pipeline -->
      <Modal
        v-model:show="showCreatePipelineModal"
        size="large"
        :before-close="requestCreatePipelineModalClose"
        @close="() => (showCreatePipelineModal = false)"
      >
        <div class="p-6">
          <h3 class="text-lg font-medium mb-6">
            {{ t('PIPELINE.PIPELINES.FORM.CREATE.TITLE') }}
          </h3>
          <PipelineForm
            ref="createPipelineFormRef"
            @saved="handlePipelineCreated"
            @close="showCreatePipelineModal = false"
          />
        </div>
      </Modal>

      <!-- Montagem lazy: evita o dispatch de agents/labels do onMounted no
           load do board (o modal interno já é v-if="show") -->
      <PipelineFilter
        v-if="showFilterModal"
        :show="showFilterModal"
        :columns="columns"
        :filtered-results="filteredResults"
        :initial-filters="activeFilters"
        :current-pipeline="store.getters['pipelines/getSelectedPipeline']"
        @close="handleFilterClose"
        @apply="handleFilter"
      />

      <!-- Lost Reason Modal -->
      <LostReasonModal
        :show="showLostReasonModal"
        :card-title="pendingLostMove?.cardTitle"
        @confirm="handleLostReasonConfirm"
        @cancel="handleLostReasonCancel"
      />

      <!-- Mesmo modal do menu do card: fechar negócio é um gesto só. -->
      <MarkAsWonModal
        v-if="wonCard"
        :item="wonCard"
        :show="showWonModal"
        @success="handleWonSuccess"
        @close="handleWonCancel"
      />

      <!-- Onboarding Tour (B3 - UX/UI Sprint) -->
      <PipelineTour
        :is-active="tour.isActive.value"
        :current-step="tour.currentStep.value"
        :current-step-index="tour.currentStepIndex.value"
        :total-steps="tour.totalSteps.value"
        :progress="tour.progress.value"
        :is-first-step="tour.isFirstStep.value"
        :is-last-step="tour.isLastStep.value"
        :target-rect="tour.targetRect.value"
        @next="tour.nextStep"
        @prev="tour.prevStep"
        @skip="tour.skipTour"
        @complete="tour.completeTour"
      />
    </template>
  </div>
</template>
